// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Shared @-mention composer.
 *
 * Extracted from CommentThread (2026-08-21) when support case comments needed the same control.
 * L-067: when a control exists on two surfaces, share the mounted unit rather than growing a
 * second one. The support version was briefly written as a fork and reproduced this file's
 * "only submit mentions whose @Name is still in the text" rule independently — which is exactly
 * the drift the lesson is about. Deleted in favour of this.
 *
 * Two seams keep it reusable without branching on the caller:
 *   • `search`      — where candidates come from. Rx/patient threads search tenant users; a
 *                     support case searches its own scoped set (Owl staff + that ticket's
 *                     pharmacy), resolved server-side from the ticket.
 *   • `disabledFor` — lets a surface refuse a candidate WITH A REASON, shown in the menu rather
 *                     than hidden. Support uses it for "a customer cannot be tagged on an
 *                     internal note"; an absent option is a mystery, a disabled one is an answer.
 */
import { useEffect, useRef, useState } from 'react'
import * as api from '../../api/client'

/**
 * Textarea with inline @-mention autocomplete. Typing "@" opens a dropdown of tenant users
 * (same pharmacy only — enforced server-side). Selecting one inserts "@Name " in place and records
 * the mention. Multiple people can be tagged in one comment. On send, only mentions whose "@Name"
 * still appears in the text are submitted.
 */
export default function MentionTextarea({ value, onChange, mentions, setMentions, placeholder, rows = 2, search, disabledFor, hint }: {
  value: string
  onChange: (v: string) => void
  mentions: api.MentionUser[]
  setMentions: (m: api.MentionUser[]) => void
  placeholder?: string
  rows?: number
  /** Candidate source. Defaults to the tenant-user search used by Rx/patient threads. */
  search?: (q: string) => Promise<api.MentionUser[]>
  /** Return a reason to block a candidate; it renders disabled with the reason shown. */
  disabledFor?: (u: api.MentionUser) => string | null
  /** Menu header text. */
  hint?: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const timer = useRef<any>(null)
  const [menu, setMenu] = useState<{ at: number; q: string } | null>(null)
  const [results, setResults] = useState<api.MentionUser[]>([])
  const [hi, setHi] = useState(0)

  useEffect(() => {
    if (!menu) { setResults([]); return }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const run = search ? search(menu.q) : api.searchMentionUsers(menu.q).then(r => r.items)
      run.then(items => { setResults(items); setHi(0) }).catch(() => setResults([]))
    }, 140)
    return () => clearTimeout(timer.current)
  }, [menu?.q])

  // Find an active "@token" immediately before the caret (token has no spaces/newlines).
  function detect(val: string, caret: number): { at: number; q: string } | null {
    let i = caret - 1
    while (i >= 0) {
      const ch = val[i]
      if (ch === '@') {
        const prev = i > 0 ? val[i - 1] : ' '
        if (i === 0 || prev === ' ' || prev === '\n') return { at: i, q: val.slice(i + 1, caret) }
        return null
      }
      if (ch === ' ' || ch === '\n') return null
      i--
    }
    return null
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value
    onChange(val)
    setMenu(detect(val, e.target.selectionStart ?? val.length))
  }

  function pick(u: api.MentionUser) {
    const caret = ref.current?.selectionStart ?? value.length
    const at = menu?.at ?? caret
    const before = value.slice(0, at)
    const after = value.slice(caret)
    const insert = '@' + u.display_name + ' '
    onChange(before + insert + after)
    if (!mentions.some(m => m.id === u.id)) setMentions([...mentions, u])
    setMenu(null); setResults([])
    setTimeout(() => {
      const pos = (before + insert).length
      ref.current?.focus()
      ref.current?.setSelectionRange(pos, pos)
    }, 0)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!menu || results.length === 0) return
    // Never let Enter select a candidate the surface has refused.
    if ((e.key === 'Enter' || e.key === 'Tab') && disabledFor?.(results[hi])) { e.preventDefault(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)) }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(results[hi]) }
    else if (e.key === 'Escape') { setMenu(null) }
  }

  return (
    <div style={{ position: 'relative' }}>
      <textarea ref={ref} value={value} onChange={handleChange} onKeyDown={onKeyDown} rows={rows}
        placeholder={placeholder}
        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 10px', fontFamily: 'inherit', fontSize: 14, resize: 'vertical' }} />
      {menu && results.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 30, left: 0, right: 0, bottom: '100%', marginBottom: 4, background: 'var(--color-surface,#fff)', border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 -4px 14px rgba(0,0,0,.10)', overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
          <div style={{ fontSize: 10.5, color: 'var(--color-text-mute)', padding: '5px 10px', borderBottom: '1px solid var(--color-border)' }}>{hint ?? 'Tag a teammate'}</div>
          {results.map((u, idx) => {
            const blocked = disabledFor?.(u) ?? null
            return (
              <button key={u.id} disabled={!!blocked}
                onMouseDown={e => { e.preventDefault(); if (!blocked) pick(u) }}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: idx === hi && !blocked ? 'var(--color-primary-soft,#eff6ff)' : 'none', border: 'none', padding: '7px 10px', cursor: blocked ? 'not-allowed' : 'pointer', fontSize: 12.5, fontFamily: 'inherit', opacity: blocked ? .5 : 1 }}>
                <span style={{ fontWeight: 600 }}>{u.display_name}</span>
                <span style={{ color: 'var(--color-text-mute)', fontSize: 11, marginLeft: 6 }}>{u.email}</span>
                {blocked && <span style={{ color: '#b45309', fontSize: 11, marginLeft: 6 }}>{blocked}</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
